import React, { useCallback, useRef } from 'react';
import CSSModules from 'react-css-modules';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { compose, renderNothing, branch } from 'recompose';
import { injectIntl } from 'react-intl';

import ActionConfirmation from '../ActionConfirmation';
import PopoverMenu from '../PopoverMenu';
import { CropModal } from '../CropModal';
import Icon from '../../Icon';
import { IDS } from '../../../constants/ids';
import { useFileManagement, ALLOWED_FORMAT } from './utils';
import { useToggleFlag } from '../../../hocs/useToogleFlag';
import styles from './styles.styl';

const ImageManagerImpl = ({
  intl: { formatMessage },
  onImageRemove,
  onImageCrop,
  aspectRatio,
  removeTitle,
  cropTitle,
  cropTip,
  rounded,
  style,
}) => {
  const [file, setFile, handleFileChange] = useFileManagement();
  const [confirmOpened, openConfirm, closeConfirm] = useToggleFlag(false);
  const [menuOpened, openMenu, closeMenu] = useToggleFlag(false);
  const handleCloseCrop = useCallback(() => setFile(undefined), []);
  const anchorEl = useRef();
  const fileRef = useRef();

  const menuItems = [
    {
      id: IDS.COMMON.IMAGE_MANAGER_UPDATE,
      text: formatMessage({ id: 'profile.avatar.update' }),
      icon: 'image',
      onClick: () => {
        closeMenu();
        fileRef.current.click();
      },
    },
    {
      id: IDS.COMMON.IMAGE_MANAGER_REMOVE,
      text: formatMessage({ id: 'profile.avatar.remove' }),
      icon: 'remove',
      onClick: () => {
        closeMenu();
        openConfirm();
      },
    },
  ];

  return (
    <div styleName={cn('root')} className="img-manager" style={style}>
      <button
        id={IDS.COMMON.IMAGE_MANAGER_EDIT}
        styleName="button"
        ref={anchorEl}
        onClick={openMenu}
        type="button"
      >
        <Icon name="edit" />
      </button>

      <PopoverMenu
        size="small"
        isOpen={menuOpened}
        anchorEl={anchorEl.current}
        onClose={closeMenu}
        items={menuItems}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <CropModal
        onImageCrop={onImageCrop}
        aspectRatio={aspectRatio}
        onClose={handleCloseCrop}
        rounded={rounded}
        title={cropTitle}
        tip={cropTip}
        image={file}
      />

      <ActionConfirmation
        opened={confirmOpened}
        onSubmit={onImageRemove}
        onClose={closeConfirm}
        actionButtonText={formatMessage({ id: 'common.remove' })}
        intl={{ formatMessage }}
        title={removeTitle}
        width="auto"
      />

      <input
        type="file"
        accept={ALLOWED_FORMAT.join(', ')}
        ref={fileRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export const ImageManager = compose(
  injectIntl,
  branch(({ hidden }) => hidden, renderNothing),
  CSSModules(styles, { allowMultiple: true }),
)(ImageManagerImpl);

const propsTypes = {
  onImageCrop: PropTypes.func.isRequired,
  onImageRemove: PropTypes.func.isRequired,
  cropTitle: PropTypes.string.isRequired,
  removeTitle: PropTypes.string,
  rounded: PropTypes.bool,
  hidden: PropTypes.bool,
  cropTip: PropTypes.string,
  aspectRatio: PropTypes.number,
};

const defaultProps = {
  removeTitle: '',
  cropTip: '',
  hidden: false,
  rounded: false,
  aspectRatio: 1,
};

ImageManagerImpl.propTypes = {
  intl: PropTypes.object.isRequired,
  style: PropTypes.object.isRequired,
  ...propsTypes,
};

ImageManagerImpl.defaultProps = { ...defaultProps };

ImageManager.propTypes = propsTypes;
ImageManager.defaultProps = defaultProps;



// WEBPACK FOOTER //
// ./src/components/common/ImageManager/index.jsx